import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { getBooksAgree } from "../components/MarketLean";

const SlipContext = createContext(null);
const STORAGE_KEY = "pm_slip_legs";

// Slip leg keeps the AI projection + which book's line it beats, so slips read projection-first.
const toLeg = (prop, lean) => {
  const ba = getBooksAgree({ ...prop, lean });
  return {
    prop_id: prop.id,
    player_name: prop.player_name,
    metric: prop.metric,
    line: prop.line,
    lean,
    odds: lean === "OVER" ? prop.over_odds : prop.under_odds,
    projection: prop.projection ?? null,
    bookmaker: prop.bookmaker || null,
    books_agree: ba ? ba.agree : prop.books_agree ?? null,
    books_total: ba ? ba.total : prop.books_total ?? null,
    team_abbr: prop.team_abbr,
    opponent_abbr: prop.opponent_abbr,
    game_label: prop.game_label || null,
    sport: prop.sport,
  };
};

export function SlipProvider({ children }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [legs, setLegs] = useState([]);
  const [open, setOpen] = useState(false);
  const loadedFor = useRef(null);

  // Each account has its own in-progress slip, stored on the server.
  useEffect(() => {
    localStorage.removeItem(STORAGE_KEY);
    if (!user) {
      loadedFor.current = null;
      setLegs([]);
      return;
    }
    loadedFor.current = null;
    api.get("/slip/current")
      .then((r) => setLegs(r.data.legs || []))
      .catch(() => setLegs([]))
      .finally(() => { loadedFor.current = user.id; });
  }, [user]);

  useEffect(() => {
    if (!user || loadedFor.current !== user.id) return;
    const t = setTimeout(() => api.put("/slip/current", { legs }).catch(() => {}), 400);
    return () => clearTimeout(t);
  }, [legs, user]);

  const needUser = () => {
    if (user) return false;
    toast.error("Sign in to build your own slip", { action: { label: "Sign in", onClick: () => nav("/login") } });
    return true;
  };

  const addLeg = (prop, lean) => {
    if (needUser()) return;
    const leg = toLeg(prop, lean);
    setLegs((prev) => {
      const exists = prev.find((l) => l.prop_id === prop.id);
      if (exists) {
        toast.info(`${prop.player_name} ${prop.metric} updated to ${lean}`);
        return prev.map((l) => (l.prop_id === prop.id ? leg : l));
      }
      toast.success(`Added ${prop.player_name} ${lean} ${prop.line} ${prop.metric}`);
      return [...prev, leg];
    });
    setOpen(true);
  };

  const removeLeg = (propId) => setLegs((prev) => prev.filter((l) => l.prop_id !== propId));
  const clearLegs = () => setLegs([]);
  const hasLeg = (propId) => legs.some((l) => l.prop_id === propId);

  const addRawLeg = (leg) => {
    if (needUser()) return;
    setLegs((prev) => {
      if (prev.find((l) => l.prop_id === leg.prop_id)) return prev;
      return [...prev, leg];
    });
  };

  // Replace the slip with an auto-built parlay from [{prop, lean}] selections.
  const buildParlay = (selections, label = "AI + Market consensus parlay") => {
    if (needUser()) return;
    if (!selections || !selections.length) {
      toast.info("No consensus picks to build a parlay");
      return;
    }
    const newLegs = selections.map(({ prop, lean }) => toLeg(prop, lean));
    setLegs(newLegs);
    toast.success(`Built a ${newLegs.length}-leg ${label}`);
    setOpen(true);
  };

  const addParlay = (parlayLegs) => {
    if (needUser()) return;
    const propLegs = parlayLegs.filter((l) => l.type === "prop" && l.prop_id);
    if (!propLegs.length) {
      toast.info("This parlay's winner legs can't be added as props");
      return;
    }
    setLegs((prev) => {
      const existing = new Set(prev.map((l) => l.prop_id));
      const toAdd = propLegs
        .filter((l) => !existing.has(l.prop_id))
        .map((l) => ({
          prop_id: l.prop_id,
          player_name: l.player_name,
          metric: l.metric,
          line: l.line,
          lean: l.selection,
          odds: l.odds,
          projection: l.projection ?? null,
          team_abbr: l.team_abbr,
          opponent_abbr: l.opponent_abbr,
          game_label: l.game_label || null,
          sport: l.sport,
        }));
      if (!toAdd.length) {
        toast.info("Parlay legs already in your slip");
        return prev;
      }
      toast.success(`Added ${toAdd.length} AI parlay legs to your slip`);
      return [...prev, ...toAdd];
    });
    setOpen(true);
  };

  return (
    <SlipContext.Provider value={{ legs, open, setOpen, addLeg, removeLeg, clearLegs, hasLeg, addRawLeg, addParlay, buildParlay }}>
      {children}
    </SlipContext.Provider>
  );
}

export const useSlip = () => useContext(SlipContext);
